import { useMemo } from 'react';

import { ColDef } from 'ag-grid-community';
import { useSelectedView } from 'reducers/viewsSlice';
import KebabMenu from 'screens/Levelup/LevelUpCF/gridComponents/KebabMenu';
import { columnToCellMetaMapper } from 'screens/Levelup/LevelUpCF/helpers';

import { AgGridHeader } from 'uiToolkitV1/AgGridHeader';

import { FeedbackTableValues } from '../types/feedbacktable';
import { useGetFeedbackTable } from './useGetFeedbackTable';

export const useAgGridForFeedbackShareForTeam = () => {
  const view = useSelectedView();

  const { data, isLoading } = useGetFeedbackTable({
    cohortId: view?.id,
    feedbackStatus: 'RECEIVED',
    limit: 1000,
    offset: 0,
  });

  const { values } = data ?? {};

  const columns = useMemo(
    () => [
      {
        id: 'employee',
        displayName: 'Shared by',
        uuidType: 'EMPLOYEE_NAME',
      },
      {
        id: 'sharedFor',
        displayName: 'Shared for',
        uuidType: 'MULTIPLE_EMPLOYEES_2',
      },
      {
        id: 'feedbackType',
        displayName: 'Praise/Feedback',
        uuidType: 'FEEDBACK_TYPE',
      },
      {
        id: 'date',
        displayName: 'Date',
        uuidType: 'DATE',
      },
      {
        id: 'comment',
        displayName: 'Comment',
        uuidType: 'DESCRIPTION_TEXT',
      },
    ],
    []
  );

  const defaultColDef = useMemo<ColDef<FeedbackTableValues>>(
    () => ({
      sortable: true,
      menuTabs: ['filterMenuTab'],
      headerComponent: AgGridHeader,
      filter: 'agTextColumnFilter',
    }),
    []
  );

  const columnDefs = useMemo(() => {
    const dynamicColumns =
      columns?.map(({ id, displayName, uuidType }) =>
        columnToCellMetaMapper({ id, displayName, uuidType, type: 'RECEIVED' })
      ) ?? [];

    const kebabMenuColumn = {
      width: 80,
      flex: 1,
      colId: 'kebab-menu',
      // eslint-disable-next-line react/no-unstable-nested-components
      cellRenderer: (params) => <KebabMenu params={params} type="RECEIVED" />,
      valueGetter: (params) => params.data,
      field: '',
      headerName: '',
      lockPosition: 'right',
      pinned: 'right',
      suppressHeaderMenuButton: true,
      cellClass: 'locked-col',
    };

    return [...dynamicColumns, kebabMenuColumn];
  }, [columns]);

  return { columnDefs, defaultColDef, rowData: values, isLoading };
};
